<template>
    <div class="sidebar-container">
        <div class="logout-modal" v-if="loading">
            <div class="loading"></div>
        </div>

        <div class="sidebar-top">
            <div class="sidebar-brand">
                <img src="../../assets/images/logoIcon.png" class="sidebar-brand__image" alt="HovaStore" />
                <div>
                    <div class="sidebar-brand__title">HovaStore</div>
                    <div class="sidebar-brand__subtitle">{{ subtitle || "Workspace" }}</div>
                </div>
            </div>

            <hs-button class="sidebar-close" variant="secondary" icon="x" icon-only @click="close" />
        </div>

        <div class="sidebar-store-card" v-if="currentStore">
            <div class="sidebar-store-card__icon">
                <hs-icon name="shopping-bag" :size="17" />
            </div>
            <div class="sidebar-store-card__body">
                <div class="sidebar-store-card__name">{{ storeDisplayName }}</div>
                <div class="sidebar-store-card__meta">{{ storeMetaLabel }}</div>
            </div>
            <hs-badge class="sidebar-store-card__badge" :tone="storeRoleTone" size="sm">
                {{ storeRoleLabel }}
            </hs-badge>
        </div>

        <button class="sidebar-create-store-btn" type="button" @click="openCreateStoreModal">
            <hs-icon class="sidebar-create-store-btn__icon" name="plus" :size="15" />
            <span class="sidebar-create-store-btn__text">Create store</span>
        </button>

        <div class="sidebar-nav-shell">
            <div class="sidebar-nav-group">
                <div class="sidebar-nav-title">Workspace</div>

                <div class="sidebar-nav-links">
                    <button
                        class="sidebar-nav-link"
                        v-for="item in workspaceMenus"
                        :key="item.icon"
                        :class="{ 'sidebar-nav-link--active': isRouteActive(item) }"
                        @click="navigateTo(item.link)"
                    >
                        <div class="sidebar-nav-link__icon">
                            <hs-icon :name="item.icon" :size="16" />
                        </div>
                        <div class="sidebar-nav-link__title">{{ item.title }}</div>
                    </button>
                </div>
            </div>

            <div class="sidebar-nav-group" v-if="adminMenus.length">
                <div class="sidebar-nav-title">Management</div>

                <div class="sidebar-nav-links">
                    <button
                        class="sidebar-nav-link"
                        v-for="item in adminMenus"
                        :key="item.icon"
                        :class="{ 'sidebar-nav-link--active': isRouteActive(item) }"
                        @click="navigateTo(item.link)"
                    >
                        <div class="sidebar-nav-link__icon">
                            <hs-icon :name="item.icon" :size="16" />
                        </div>
                        <div class="sidebar-nav-link__title">{{ item.title }}</div>
                    </button>
                </div>
            </div>
        </div>

        <div class="sidebar-build-info" title="Application version and source commit">
            {{ buildLabel }}
        </div>

        <div class="sidebar-user-card">
            <button class="sidebar-user-avatar" @click="toggleProfileModal()" :title="abbreviateName(user?.names)">
                {{ userInitial }}
            </button>

            <div class="sidebar-user-info">
                <div class="sidebar-user-name">{{ abbreviateName(user?.names) }}</div>
                <div class="sidebar-user-phone">{{ user?.phone_number }}</div>
            </div>

            <div class="sidebar-user-actions">
                <hs-tooltip text="Profile settings" placement="right">
                    <button @click="toggleProfileModal()" class="sidebar-user-action-btn" aria-label="Open settings">
                        <hs-icon name="settings" :size="14" />
                    </button>
                </hs-tooltip>

                <hs-tooltip text="Logout" placement="right">
                    <button
                        class="sidebar-user-action-btn sidebar-user-action-btn--danger"
                        @click="showLogoutConfirm = true"
                        aria-label="Logout"
                    >
                        <hs-icon name="power" :size="14" />
                    </button>
                </hs-tooltip>
            </div>
        </div>

        <CreateStoreModal :show="showCreateStoreModal" @close="showCreateStoreModal = false"
            @created="handleStoreCreated" />

        <hs-confirm-dialog
            v-model="showLogoutConfirm"
            title="Log out of HovaStore?"
            message="You will need to sign in again to continue managing your workspace."
            confirm-text="Log out"
            cancel-text="Stay signed in"
            tone="danger"
            :loading="loading"
            @confirm="logout"
        />
    </div>
</template>

<script>
import CreateStoreModal from "@/components/modals/CreateStoreModal.vue";
import { HsBadge, HsButton, HsConfirmDialog, HsIcon, HsTooltip } from "@/components/ui";
import { buildLabel } from "@/utils/buildInfo";
import { canShowInsights } from "@/features/insights/model";

export default {
    name: "SidebarComponent",
    components: {
        CreateStoreModal,
        HsBadge,
        HsButton,
        HsConfirmDialog,
        HsIcon,
        HsTooltip,
    },
    props: ["open", "subtitle"],
    data() {
        return {
            menuConfig: [
                {
                    title: "Insights",
                    icon: "bar-chart-2",
                    link: "insights",
                    requiresInsights: true,
                },
                { title: "Briefs", icon: "home", link: "store_dashboard" },
                { title: "Catalog", icon: "package", link: "catalog" },
                { title: "Orders", icon: "inbox", link: "orders" },
                { title: "Purchases", icon: "shopping-cart", link: "purchases" },
                { title: "Expenses", icon: "credit-card", link: "store_expenses" },
                { title: "Assets", icon: "layers", link: "store_assets" },
                { title: "Promo", icon: "tag", link: "promos" },
                { title: "Reports", icon: "file-text", link: "reports" },
                {
                    title: "Users",
                    icon: "users",
                    link: "store_users",
                    requiresStorePermissionAny: [
                        "CAN_READ_USERS",
                        "CAN_WRITE_USERS",
                        "CAN_MANAGE_USERS",
                    ],
                },
                { title: "Settings", icon: "settings", link: "settings" },
                {
                    title: "Admin",
                    icon: "shield",
                    link: "store_admin",
                    requiresAdmin: true,
                },
            ],
            loading: false,
            showCreateStoreModal: false,
            showLogoutConfirm: false,
            insightsEligibility: null,
            buildLabel,
        };
    },
    computed: {
        user() {
            return this.$store.getters.getAuthenticatedUser;
        },
        hasAdminPermissions() {
            return this.$store.getters.hasAdminPermissions;
        },
        menus() {
            return this.menuConfig.filter((item) => {
                if (item.requiresAdmin && !this.hasAdminPermissions) {
                    return false;
                }
                if (
                    item.requiresInsights &&
                    !canShowInsights(
                        this.user,
                        this.$store.getters.getUserStores || [],
                        this.insightsEligibility,
                    )
                ) {
                    return false;
                }
                if (
                    item.requiresStorePermissionAny &&
                    !this.hasAnyStorePermission(item.requiresStorePermissionAny)
                ) {
                    return false;
                }
                return true;
            });
        },
        workspaceMenus() {
            return this.menus.filter(
                (item) => !["store_users", "settings", "store_admin"].includes(item.link),
            );
        },
        adminMenus() {
            return this.menus.filter((item) =>
                ["store_users", "settings", "store_admin"].includes(item.link),
            );
        },
        selectedStoreId() {
            const selectedStore = this.$store.state.dashboard?.selectedStore;
            return typeof selectedStore === "object" && selectedStore !== null
                ? selectedStore.id
                : selectedStore;
        },
        currentStore() {
            if (!this.selectedStoreId) {
                return null;
            }

            const resolver = this.$store.getters.getStoreById;
            if (typeof resolver === "function") {
                const store = resolver(this.selectedStoreId);
                if (store) {
                    return store;
                }
            }

            const stores = this.$store.getters.getUserStores || [];
            return stores.find(
                (store) => String(store.id) === String(this.selectedStoreId),
            );
        },
        currentStorePermissions() {
            const permissions = Array.isArray(this.currentStore?.permissions)
                ? this.currentStore.permissions
                : [];

            return permissions.map((permission) =>
                String(permission || "").toUpperCase(),
            );
        },
        storeDisplayName() {
            return (
                this.currentStore?.name ||
                this.currentStore?.store_name ||
                this.currentStore?.business_name ||
                "Selected store"
            );
        },
        storeMetaLabel() {
            const merchantCode =
                this.currentStore?.merchant_code ||
                this.currentStore?.merchantCode ||
                this.currentStore?.tin;

            if (merchantCode) {
                return `Merchant ${merchantCode}`;
            }

            if (this.selectedStoreId) {
                return `Store ${this.selectedStoreId}`;
            }

            return "Active workspace";
        },
        storeRoleLabel() {
            if (this.currentStore?.isOwner) {
                return "Owner";
            }

            if (this.hasAdminPermissions) {
                return "Admin";
            }

            return "Member";
        },
        storeRoleTone() {
            if (this.currentStore?.isOwner) {
                return "success";
            }

            if (this.hasAdminPermissions) {
                return "primary";
            }

            return "neutral";
        },
        userInitial() {
            return this.abbreviateName(this.user?.names)?.charAt(0).toUpperCase() || "U";
        },
    },
    methods: {
        updateInsightsEligibility() {
            try {
                const value = sessionStorage.getItem("insights-eligible");
                this.insightsEligibility =
                    value === null ? null : value === "true";
            } catch (_) {
                this.insightsEligibility = null;
            }
        },
        close() {
            this.$emit("close");
        },
        isRouteActive(item) {
            const routeName = String(this.$route?.name || "");
            if (routeName === item.link) {
                return true;
            }

            if (item.link === "orders" && routeName.startsWith("orders_")) {
                return true;
            }
            if (item.link === "purchases" && routeName.startsWith("purchases_")) {
                return true;
            }

            return false;
        },
        navigateTo(routeName) {
            if (routeName && routeName.trim()) {
                this.close();

                const storeSpecificRoutes = [
                    "catalog",
                    "store_dashboard",
                    "promos",
                    "reports",
                    "store_users",
                    "orders",
                    "orders_transactions",
                    "orders_tickets",
                    "orders_incoming_orders",
                    "orders_fulfillment_tickets",
                    "orders_product_mappings",
                    "purchases",
                    "purchases_suppliers",
                    "purchases_purchase_orders",
                    "purchases_purchase_order_detail",
                    "purchases_receipts",
                    "store_expenses",
                    "store_assets",
                    "settings",
                    "store_admin",
                ];

                if (storeSpecificRoutes.includes(routeName)) {
                    let normalizedRoute = routeName;
                    if (routeName === "orders") normalizedRoute = "orders_transactions";
                    if (routeName === "purchases")
                        normalizedRoute = "purchases_purchase_orders";
                    const targetStoreId = this.selectedStoreId
                        || this.$route?.params?.storeId
                        || (this.$store.getters.getUserStores || []).find((store) => store?.id)?.id;
                    if (targetStoreId) {
                        this.$router.push({
                            name: normalizedRoute,
                            params: { storeId: targetStoreId },
                        });
                    } else if (routeName === "purchases") {
                        this.$router.push({ name: "admin_purchases" });
                    } else if (routeName === "store_expenses") {
                        this.$router.push({ name: "admin_expenses" });
                    } else if (routeName === "reports") {
                        this.$router.push({ name: "no_stores" });
                    } else if (routeName === "store_admin") {
                        const stores = this.$store.getters.getUserStores || [];
                        const targetStoreId = stores[0]?.id;
                        if (targetStoreId) {
                            this.$router.push({
                                name: "store_admin",
                                params: { storeId: targetStoreId },
                            });
                        } else {
                            this.$router.push({ name: "no_stores" });
                        }
                    } else {
                        this.$router.push({ name: "no_stores" });
                    }
                } else {
                    this.$router.push({ name: routeName });
                }
            }
        },
        toggleProfileModal() {
            this.$emit("toggle-profilemodal");
        },
        openCreateStoreModal() {
            this.showCreateStoreModal = true;
        },
        async handleStoreCreated(newStore) {
            this.showCreateStoreModal = false;

            try {
                const { stores: fetchedStores = [] } = await this.$store.dispatch(
                    "getAllUsersStore",
                );
                localStorage.setItem("stores", JSON.stringify(fetchedStores));
            } catch (error) {
                console.error("Failed to refresh stores after creation:", error);
            }

            if (newStore?.id) {
                this.$store.commit("SET_SELECTED_STORE", newStore.id);
                window.dispatchEvent(new CustomEvent("hovastore:stores-updated"));

                if (this.$route?.name && this.$route?.params?.storeId) {
                    this.$router.replace({
                        name: this.$route.name,
                        params: {
                            ...this.$route.params,
                            storeId: newStore.id,
                        },
                        query: this.$route.query,
                        hash: this.$route.hash,
                    });
                } else {
                    this.$router.push({
                        name: "insights",
                    });
                }
            }
        },
        hasAnyStorePermission(permissions) {
            if (this.hasAdminPermissions || this.currentStore?.isOwner) {
                return true;
            }

            return permissions.some((permission) =>
                this.currentStorePermissions.includes(String(permission).toUpperCase()),
            );
        },
        logout() {
            this.loading = true;
            this.$store
                .dispatch("logoutUser")
                .catch(() => {
                    // Ignore error; still proceed to redirect
                })
                .finally(() => {
                    this.loading = false;
                    this.showLogoutConfirm = false;
                    try {
                        localStorage.removeItem("vuex");
                        localStorage.removeItem("stores");
                        localStorage.removeItem("ebm_background_tasks");
                    } catch (e) {
                        // ignore
                    }
                    this.$router.push("/");
                });
        },
        abbreviateName(fullName) {
            const names = fullName?.split(" ");

            if (names && names.length >= 2) {
                const firstNameInitial = names[0][0].toUpperCase();
                const lastName = names[names.length - 1];
                return `${firstNameInitial}. ${lastName}`;
            }

            return fullName || "User";
        },
    },
    mounted() {
        this.$store.dispatch("getUserInfo");
        this.updateInsightsEligibility();
        window.addEventListener(
            "hovastore:insights-eligibility",
            this.updateInsightsEligibility,
        );
    },
    beforeUnmount() {
        window.removeEventListener(
            "hovastore:insights-eligibility",
            this.updateInsightsEligibility,
        );
    },
};
</script>

<style lang="scss" scoped>
.sidebar-container {
    background:
        linear-gradient(180deg, var(--hs-surface, #ffffff) 0%, color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 78%, #ffffff) 100%);
    border-right: 1px solid var(--hs-border, #e2e8f0);
    box-shadow: none;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    height: 100vh;
    min-height: 0;
    overflow: visible;
    padding: 0.9rem;
    position: relative;
}

.logout-modal {
    align-items: center;
    background: rgba(15, 23, 42, 0.54);
    border-radius: 0;
    display: flex;
    inset: 0;
    justify-content: center;
    position: absolute;
    z-index: 30;
}

.loading {
    width: 35px;
    height: 35px;
    border: 2px solid transparent;
    border-radius: 50%;
    border-top: 2px solid #fff;
    border-right: 2px solid #fff;
    border-left: 2px solid #fff;
    animation: rotate 1s infinite linear;
}

@keyframes rotate {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.sidebar-top {
    align-items: center;
    display: flex;
    justify-content: space-between;
    min-height: 2.9rem;
}

.sidebar-brand {
    align-items: center;
    display: flex;
    gap: 0.72rem;
    min-width: 0;
}

.sidebar-brand__image {
    background: var(--hs-surface, #ffffff);
    border: 1px solid var(--hs-border, #e2e8f0);
    border-radius: 12px;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
    height: 42px;
    object-fit: cover;
    width: 42px;
}

.sidebar-brand__title {
    color: var(--hs-text, #0f172a);
    font-size: 0.96rem;
    font-weight: 950;
    line-height: 1.12;
}

.sidebar-brand__subtitle {
    color: var(--hs-muted, #64748b);
    font-size: 0.72rem;
    font-weight: 750;
    line-height: 1.25;
    margin-top: 0.15rem;
}

.sidebar-close {
    display: none !important;
}

.sidebar-store-card {
    align-items: center;
    background: color-mix(in srgb, var(--hs-surface, #ffffff) 92%, var(--hs-primary, #3293ec) 8%);
    border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 16%, var(--hs-border, #e2e8f0));
    border-radius: 14px;
    display: grid;
    gap: 0.65rem;
    grid-template-columns: auto minmax(0, 1fr) auto;
    padding: 0.72rem;
}

.sidebar-store-card__icon {
    align-items: center;
    background: var(--hs-surface, #ffffff);
    border: 1px solid var(--hs-border, #e2e8f0);
    border-radius: 11px;
    color: var(--hs-primary, #3293ec);
    display: inline-flex;
    height: 2.25rem;
    justify-content: center;
    width: 2.25rem;
}

.sidebar-store-card__body {
    min-width: 0;
}

.sidebar-store-card__name {
    color: var(--hs-text, #0f172a);
    font-size: 0.83rem;
    font-weight: 950;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sidebar-store-card__meta {
    color: var(--hs-muted, #64748b);
    font-size: 0.68rem;
    font-weight: 750;
    line-height: 1.25;
    margin-top: 0.2rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sidebar-store-card__badge {
    justify-self: end;
}

.sidebar-create-store-btn {
    align-items: center;
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 9%, transparent);
    border: 1px dashed color-mix(in srgb, var(--hs-primary, #3293ec) 32%, var(--hs-border, #e2e8f0));
    border-radius: 13px;
    color: var(--hs-primary-strong, #2563eb);
    display: flex;
    font-size: 0.8rem;
    font-weight: 900;
    gap: 0.55rem;
    min-height: 2.55rem;
    padding: 0 0.8rem;
    text-align: left;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
    width: 100%;
}

.sidebar-create-store-btn:hover {
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 13%, transparent);
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 48%, var(--hs-border, #e2e8f0));
    color: var(--hs-primary-strong, #1d4ed8);
}

.sidebar-create-store-btn:focus-visible {
    outline: 3px solid color-mix(in srgb, var(--hs-primary, #3293ec) 22%, transparent);
    outline-offset: 2px;
}

.sidebar-create-store-btn__icon {
    align-items: center;
    background: var(--hs-surface, #ffffff);
    border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 18%, var(--hs-border, #e2e8f0));
    border-radius: 10px;
    display: inline-flex;
    height: 1.75rem;
    justify-content: center;
    width: 1.75rem;
}

.sidebar-create-store-btn__text {
    min-width: 0;
}

.sidebar-nav-shell {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 1rem;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.1rem;
    scrollbar-color: color-mix(in srgb, var(--hs-muted, #64748b) 24%, transparent) transparent;
    scrollbar-width: thin;
}

.sidebar-nav-shell::-webkit-scrollbar {
    width: 6px;
}

.sidebar-nav-shell::-webkit-scrollbar-track {
    background: transparent;
}

.sidebar-nav-shell::-webkit-scrollbar-thumb {
    background: color-mix(in srgb, var(--hs-muted, #64748b) 24%, transparent);
    border-radius: 999px;
}

.sidebar-build-info {
    color: var(--hs-muted, #64748b);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.63rem;
    line-height: 1;
    opacity: 0.68;
    padding: 0 0.45rem;
}

.sidebar-nav-group {
    display: flex;
    flex-direction: column;
    gap: 0.32rem;
}

.sidebar-nav-title {
    color: var(--hs-muted, #64748b);
    font-size: 0.68rem;
    font-weight: 900;
    letter-spacing: 0;
    line-height: 1;
    padding: 0 0.45rem 0.25rem;
    text-transform: uppercase;
}

.sidebar-nav-links {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-height: 0;
    overflow: visible;
    padding: 0;
}

.sidebar-nav-link {
    align-items: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 12px;
    color: var(--hs-muted, #64748b);
    display: grid;
    gap: 0.68rem;
    grid-template-columns: 2rem minmax(0, 1fr);
    min-height: 2.55rem;
    padding: 0.28rem 0.52rem;
    position: relative;
    text-align: left;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
    width: 100%;
}

.sidebar-nav-link::before {
    background: var(--hs-primary, #3293ec);
    border-radius: 999px;
    content: "";
    height: 1.45rem;
    left: -0.42rem;
    opacity: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%) scaleY(0.45);
    transition: opacity 0.18s ease, transform 0.18s ease;
    width: 3px;
}

.sidebar-nav-link__icon {
    align-items: center;
    background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 88%, transparent);
    border: 1px solid transparent;
    border-radius: 10px;
    color: inherit;
    display: inline-flex;
    height: 2rem;
    justify-content: center;
    width: 2rem;
}

.sidebar-nav-link__title {
    font-size: 0.82rem;
    font-weight: 850;
    line-height: 1.2;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sidebar-nav-link:hover {
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 7%, transparent);
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 14%, var(--hs-border, #e2e8f0));
    color: var(--hs-text, #0f172a);
    transform: none;
}

.sidebar-nav-link:hover .sidebar-nav-link__icon {
    background: var(--hs-surface, #ffffff);
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 13%, var(--hs-border, #e2e8f0));
    color: var(--hs-primary, #3293ec);
}

.sidebar-nav-link--active,
.sidebar-nav-link--active:hover {
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, var(--hs-surface, #ffffff));
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 22%, var(--hs-border, #e2e8f0));
    box-shadow: none;
    color: var(--hs-primary-strong, #2563eb);
}

.sidebar-nav-link--active::before {
    opacity: 1;
    transform: translateY(-50%) scaleY(1);
}

.sidebar-nav-link--active .sidebar-nav-link__icon {
    background: var(--hs-surface, #ffffff);
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 18%, var(--hs-border, #e2e8f0));
    color: var(--hs-primary-strong, #2563eb);
}

.sidebar-user-card {
    align-items: center;
    background: var(--hs-surface, #ffffff);
    border: 1px solid var(--hs-border, #e2e8f0);
    border-radius: 14px;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
    column-gap: 0.68rem;
    display: grid;
    grid-template-columns: auto 1fr auto;
    overflow: visible;
    padding: 0.72rem;
    position: relative;
    flex-shrink: 0;
}

.sidebar-user-card::before {
    display: none;
}

.sidebar-user-avatar {
    align-items: center;
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 13%, transparent);
    border: 1px solid color-mix(in srgb, var(--hs-primary, #3293ec) 24%, var(--hs-border, #e2e8f0));
    border-radius: 12px;
    color: var(--hs-primary-strong, #2563eb);
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 40px;
    font-size: 0.8rem;
    font-weight: 950;
    height: 40px;
    justify-content: center;
    position: relative;
    z-index: 1;
    transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
    width: 40px;
}

.sidebar-user-avatar:hover {
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 18%, transparent);
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 36%, var(--hs-border, #e2e8f0));
    transform: translateY(-1px);
}

.sidebar-user-info {
    min-width: 0;
    position: relative;
    z-index: 1;
}

.sidebar-user-name {
    color: var(--hs-text, #0f172a);
    font-size: 0.84rem;
    font-weight: 950;
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sidebar-user-phone {
    color: var(--hs-muted, #64748b);
    font-size: 0.7rem;
    font-weight: 750;
    margin-top: 0.24rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sidebar-user-actions {
    align-items: center;
    display: flex;
    flex: 0 0 auto;
    gap: 0.32rem;
    position: relative;
    z-index: 1;
}

.sidebar-user-action-btn {
    align-items: center;
    background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 82%, transparent);
    border: 1px solid var(--hs-border, #e2e8f0);
    border-radius: 10px;
    color: var(--hs-muted, #64748b);
    cursor: pointer;
    display: inline-flex;
    height: 32px;
    justify-content: center;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
    width: 32px;
}

.sidebar-user-action-btn:hover {
    background: color-mix(in srgb, var(--hs-primary, #3293ec) 9%, transparent);
    border-color: color-mix(in srgb, var(--hs-primary, #3293ec) 20%, var(--hs-border, #e2e8f0));
    color: var(--hs-primary-strong, #2563eb);
}

.sidebar-user-action-btn--danger:hover {
    background: color-mix(in srgb, #ef4444 10%, transparent);
    border-color: color-mix(in srgb, #ef4444 24%, var(--hs-border, #e2e8f0));
    color: #dc2626;
}

@media (max-width: 1023px) {
    .sidebar-close {
        display: inline-flex !important;
    }
}
</style>
